import React, { ReactNode, useState, useEffect, ReactElement } from 'react';
import { DndBarNode } from '../../dndBar';
import { registerNode, AdditionalNode, AdditionalComponentProps, ValidateClaimFn } from '.././additionalNodes';
import {guidGenerator} from '../../../../utils/utils';
import { JE_JsonValidationContext } from '../../../../jsonParsing/interfaces';
import {FQSchemaData} from '../../../../jsonParsing/fqSpecGeneration';
import useComponentUIHook from '../../componentUI/useComponentUIHook';
import TemplatizedFieldComponent, {getTemplatizedValue, TemplatizedValue} from '../../componentUI/templatizedFieldComponent'; 
import SessionManagement, {sessionFieldsAreValid} from '../../componentUI/sessionManagement';
import { jsonEditorStyles } from '../../jsonEditorStyles';
import { SelectComponent } from '../../../uiComponents';
import { Typography, SelectChangeEvent, Button, FormControl } from '@mui/material';
import { OptionProps } from '../../../../view-models';
import { cloneDeep } from 'lodash';
import {BuildSettings, extractTemplatizableField} from '../../../../jsonParsing/utils';
import { 
  createFQClaimXform, JE_FQClaimXformSpec,
  JE_FQContentDefinitionSpec,
  fetchDefaultFQContentDefinitionRef,
  JE_FQClaimSpec, createFQClaim,
  createFQDisplayControl,
  createFQTechnicalProfile,
  getOrCreateFQContentDefinition
} from '../../../../jsonParsing/jsonFQContext';
import { addFQOrchestrationStepOrValidationProfileRef } from '../../../../jsonParsing/fqSpecGeneration';
import constants from '../../../../constants/jsonXMLConstants';
import { createOrGetVerifyProfiles, createVerifyTOTPSelfAsserted } from './shared';


function EditNode({data, closeGritComponent, setUnsavedChanges}: AdditionalComponentProps) {
  const classes = jsonEditorStyles();
  
  const [disableSubmit, setDisableSubmit] = useState(true);
  const {
    modifiedData,
    setModifiedData,
    allClaims,
    checkIfSameIgnoringSubFlows,
    modifiedTemplatizations,
    setModifiedTemplatizations
  } = useComponentUIHook(data);
  
  useEffect(() => {
    try {
      const _res: boolean = checkIfSameIgnoringSubFlows();
      setDisableSubmit(
        _res ||
          (!(
            modifiedData['idClaim']
          )) ||
          (!sessionFieldsAreValid(modifiedData))
      );
      setUnsavedChanges(_res);
    } catch( error ) {
      console.log(error)
    }
  }, [modifiedData]);

  return <div className={classes.flexCol}>
      <div className={classes.flexRow}>
        <TemplatizedFieldComponent
          modifiedTemplatizations={modifiedTemplatizations}
          setModifiedTemplatizations={setModifiedTemplatizations}
          data={{
            fieldPath: 'contentDefinition',
            label: "Content Definition",
            style: { margin: 1, width: 600 },
            value: getTemplatizedValue(modifiedData['contentDefinition']),
            variant: 'text'
          }}
          changeHandler={(e: TemplatizedValue) => {
            setModifiedData({ ...modifiedData, contentDefinition: { ...e } });
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select claim containing user's objectId for use in credential generation:
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              objectIdClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.objectIdClaim ? modifiedData.objectIdClaim : '',
            label: 'objectId Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      <Typography variant="body1" sx={{ pt: 1 }}>
        Select Claim to identify user to Authenticator MFA service, must contain user principal name
      </Typography>
      <div className={classes.flexRow} style={{ alignItems: 'center' }}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setModifiedData({
              ...modifiedData,
              idClaim: e.target.value,
            });
          }}
          data={{
            value: modifiedData.idClaim ? modifiedData.idClaim : '',
            label: 'principalID Claim',
            required: true,
            style: { margin: 1, width: 200 },
            options: Object.keys(allClaims)
              .filter((c) => {
                return allClaims[c]?.dataType === 'string';
              })
              .reduce((acc: OptionProps[], curVal: string) => {
                return acc.concat({ label: curVal, value: curVal });
              }, [])
              .sort((a: OptionProps, b: OptionProps) => {
                return a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1;
              }),
          }}
        />
      </div>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={disableSubmit}
        sx={{ mt: 3, mb: 2 }}
        onClick={() => {
          closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
        }}>
        Save
      </Button>
  </div>
}

interface NodeData {
  contentDefinitionUrl: string;
  contentDefinition: {
   displayName: "Content Definition",
   value: ""
  },
  idClaim: string;
  objectIdClaim: string;
  sessionManagement: any //TODO import session management
}

class AuthenticatorMFA extends AdditionalNode {
  componentType = "authenticatorAppMFA";
  label =  "Authenticator App MFA" 
  
  getDndComponent(): ReactNode {
    return <DndBarNode componentType={this.componentType} label = {this.label}/>
  }
  
  getOutputClaims(nodeData: any, validationContext: JE_JsonValidationContext): string[] {
      return [nodeData.idClaim, nodeData.objectIdClaim]
  }
  
  EditNodeUI(props: AdditionalComponentProps): React.ReactElement<any, any> {
    return <EditNode {...props}/>
  }

  getJSONCompDef() {
    return {
      nodeType: 'process',
      nodeSubType: 'default',
      label: this.label,
      idClaim: "",
      availabilityClaim:"",
      objectIdClaim: "",
      contentDefinition: {
        displayName: 'Content Definition',
      }
    }
  }

  extractFCNData(
    nodeData: any,
    componentType: string,
    buildSettings: BuildSettings,
    linkType?: string
  ){
    const data = nodeData as NodeData;
    const idClaim = data.idClaim ;
    const objectIdClaim = data.objectIdClaim;
    const sessionManagement = data.sessionManagement;
    const contentDefinitionUrl =  extractTemplatizableField(
      data.contentDefinition, buildSettings
    );
    return {
      idClaim,
      objectIdClaim,
      sessionManagement,
      contentDefinitionUrl
    };
  }

  initNodeJSON() {
    return {
      id: `${this.componentType}-` + guidGenerator(),
      componentType: this.componentType,
      idClaim: "",
      sessionManagement: {
        option: 'alwaysExecute',
        sessionId: ''
      },
      contentDefinition: {}
    }
  }

  validateJSON(
    nodeData: any,
    vCtx: JE_JsonValidationContext, 
    addErrorReport: (message: string) => void,
    validateClaim: ValidateClaimFn
  ): void {
    const data = nodeData.nodeData as NodeData;
    if (!data.idClaim) {
      addErrorReport("User principalId claim must be set!");
    } else {
      validateClaim(
        data.idClaim, 
        "User Identification Claim",
        true
      )
    }
    if (!data.objectIdClaim) {
      addErrorReport("User objectId claim must be set!");
    } else {
      validateClaim(
        data.objectIdClaim, 
        "User objectId claim",
        true
      )
    }
  }

  generateFQSchema({
    node, 
    nodes, 
    journeyId,
    parsingCtx,
    branchVarMap,
    preconditions
  }: FQSchemaData): void {
    const { 
      contentDefinitionUrl,
      objectIdClaim,
      idClaim, 
    } = node.data as NodeData;
    const ids = createOrGetVerifyProfiles(parsingCtx, objectIdClaim, idClaim);
    const MFA_TOTP = createVerifyTOTPSelfAsserted(
      parsingCtx, 
      node.sanitizedDisplayName,
      ids,
      idClaim,
      contentDefinitionUrl
    );
    addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      ids.beginVerifyId,
      "TOTP-Pre-verification-step",
      journeyId,
      branchVarMap,
      preconditions,
    );
    addFQOrchestrationStepOrValidationProfileRef(
      parsingCtx,
      node,
      nodes,
      MFA_TOTP,
      "OTP-Authenticator-App",
      journeyId,
      branchVarMap,
      preconditions,
    );
  }
  
}

registerNode(new AuthenticatorMFA());
